import noop from 'lodash/noop';

import { now } from 'Lib/utils/perfTimer';

import imageDownloadManager from '../imageDownloadManager';
import { trackImgErr } from './utils';

export const defaultImgState: State = {
  isLoading: false,
  isError: false,
  imgSrc: '',
  naturalHeight: 0,
  naturalWidth: 0,
};

export type State = {
  isLoading?: boolean;
  isError?: boolean;
  imgSrc?: string;
  naturalHeight?: number;
  naturalWidth?: number;
  downloadTime?: number;
};

class SmartImageLib {
  imageState: State;
  onChange: (params: State) => void;
  onError: (url: string, error: any) => void;
  mounted: boolean;

  constructor(onChange = noop, onError = noop) {
    this.imageState = defaultImgState;
    this.onChange = onChange;
    this.onError = onError;
    this.mounted = true;
  }

  setImgState(state: State) {
    this.imageState = {
      ...this.imageState,
      ...state,
    };
  }

  getImgState() {
    return this.imageState;
  }

  setSuccessState = (
    imgSrc: string,
    naturalWidth: number,
    naturalHeight: number,
    downloadTime: number
  ) => {
    this.setImgState({
      imgSrc,
      isError: false,
      isLoading: false,
      naturalHeight,
      naturalWidth,
      downloadTime,
    });
    this.broadcastChange();
  };

  setLoadingState = () => {
    this.setImgState({
      isError: false,
      isLoading: true,
    });
    this.broadcastChange();
  };

  setErrorState = () => {
    this.setImgState({
      isLoading: false,
      isError: true,
    });
    this.broadcastChange();
  };

  broadcastChange() {
    if (this.mounted) {
      this.onChange(this.imageState);
    }
  }

  mount() {
    this.mounted = true;
  }

  unmount() {
    this.mounted = false;
  }

  downloadImages(urls: string[]) {
    if (!urls.length) {
      return;
    }
    this.setLoadingState();
    // Reset dimensions
    this.setImgState({ naturalWidth: 0, naturalHeight: 0 });

    let errCountdown = urls.length;

    urls.forEach((url) => {
      const startTimer = now();
      imageDownloadManager.add(url, (image: HTMLImageElement, error: any) => {
        const { isLoading, naturalWidth: prevNaturalWidth = 0 } = this.getImgState();
        if (error) {
          errCountdown -= 1;
          trackImgErr(url);
          if (isLoading && errCountdown <= 0) {
            if (this.mounted) {
              this.onError(url, error);
            }
            this.setErrorState();
          }
          return;
        }

        const downloadTime = Math.round(now() - startTimer);
        const { naturalHeight, naturalWidth, src } = image;
        // condition prevents accidental setting of lower res image
        // (if it happens to be fetched after higher-res image)
        if (prevNaturalWidth <= naturalWidth && this.mounted) {
          this.setSuccessState(src, naturalWidth, naturalHeight, downloadTime);
        }
      });
    });
  }
}

export default SmartImageLib;
